import { Box, Button, HStack, Icon, Tag, Text, VStack } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { LuX } from 'react-icons/lu';

import DeleteModal from '@/components/ui/DeleteModal/DeleteModal';
import { TAgent, TAssignedCustomers } from '@/types/agents';

const MOTION_CONFIG = {
  initial: { opacity: 0, x: -20, scale: 0.8 },
  animate: { opacity: 1, x: 0, scale: 1 },
  exit: { opacity: 0, x: 20, scale: 0.8 },
  transition: { duration: 0.2, ease: [0.42, 0, 0.58, 1] as const },
};

const REMOVE_CUSTOMER_MESSAGE =
'This agent will no longer be assigned to this customer. The agent will remain available for configuration, but no one will have access to it.';

interface DetailsStageProps {
  agent: TAgent;
  assignedCustomers: TAssignedCustomers[];
  onRemoveCustomer: (customerId: string) => void;
  onAssignMoreCustomers: () => void;
  hasCustomersChanged: boolean;
  onSave: () => void;
  onNavigateToConfigure: () => void;
  isLoadingAssignAgentToCustomers: boolean;
}

const DetailsStage = ({
  agent,
  assignedCustomers,
  onRemoveCustomer,
  onAssignMoreCustomers,
  hasCustomersChanged,
  onSave,
  onNavigateToConfigure,
  isLoadingAssignAgentToCustomers,
}: DetailsStageProps) => {
  const [customerToRemove, setCustomerToRemove] = useState<string | null>(null);

  const handleRemoveClick = (customerId: string) => {
    setCustomerToRemove(customerId);
  };

  const handleConfirmRemove = () => {
    if (customerToRemove) {
      onRemoveCustomer(customerToRemove);
      setCustomerToRemove(null);
    }
  };

  const handleCloseModal = () => {
    setCustomerToRemove(null);
  };

  return (
    <VStack align="stretch" gap={6}>
      <Box>
        <Text fontSize={18} fontWeight={500} color="fg" mb={3}>
          Agent Name
        </Text>
        <Text fontSize={16} color="fg.muted" fontWeight={400}>
          {agent.name}
        </Text>
      </Box>
      <Box>
        <Text fontSize={18} fontWeight={500} color="fg" mb={3}>
          Agent Description
        </Text>
        <Text fontSize={16} color="fg.muted" fontWeight={400}>
          {agent.description}
        </Text>
      </Box>
      <Box>
        <Text fontSize={18} fontWeight={500} color="fg" mb={3}>
          Customers using this agent
        </Text>
        <Text fontSize={16} color="fg.muted" fontWeight={400} mb={3}>
          Assignment controls visibility. Only assigned customers can use this agent inside their chatbots.
        </Text>
        {assignedCustomers.length > 0 && (
          <HStack gap={2} flexWrap="wrap" mb={3}>
            <AnimatePresence mode="wait">
              {assignedCustomers.map((customer) => (
                <motion.div key={customer.customerId} {...MOTION_CONFIG} layout>
                  <Tag.Root
                    size="lg"
                    backgroundColor="purple.50"
                    border="1px solid"
                    borderColor="transparent"
                    boxShadow="none"
                    borderRadius="full"
                    px={3}
                    py={1}
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <Tag.Label fontSize={12} fontWeight={500} color="gray.700">
                      {customer.customerName}
                    </Tag.Label>
                    <Box
                      as="button"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="full"
                      boxSize={4}
                      cursor="pointer"
                      onClick={() => handleRemoveClick(customer.customerId)}
                      _hover={{ backgroundColor: 'purple.muted' }}
                    >
                      <Icon as={LuX} boxSize={3} color="purple.600" />
                    </Box>
                  </Tag.Root>
                </motion.div>
              ))}
            </AnimatePresence>
          </HStack>
        )}
        {assignedCustomers.length === 0 && (
          <Text fontSize={16} color="gray.700" fontWeight={600} width="100%" textAlign="center" mb={3}>
            No customers assigned
          </Text>
        )}
        <AnimatePresence>
          <motion.div {...MOTION_CONFIG} layout>
            <Text
              as="button"
              fontSize={14}
              color="purple.600"
              fontWeight={500}
              textDecoration="underline"
              cursor="pointer"
              onClick={onAssignMoreCustomers}
              _hover={{ color: 'purple.700' }}
            >
              Assign to more customers
            </Text>
          </motion.div>
        </AnimatePresence>
      </Box>

      <HStack gap={3} width="100%" justify="flex-end" mt={4}>
        <Button
          variant="outline"
          colorPalette="purple"
          borderColor="purple.200"
          color="purple.600"
          borderRadius={6}
          px={4}
          py={2}
          onClick={onSave}
          disabled={!hasCustomersChanged}
          _hover={{ backgroundColor: 'purple.50' }}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          loading={isLoadingAssignAgentToCustomers}
          loadingText="Saving..."
        >
          Save
        </Button>
        <Button
          variant="solid"
          colorPalette="purple"
          backgroundColor="purple.600"
          color="white"
          borderRadius={6}
          px={4}
          py={2}
          onClick={onNavigateToConfigure}
          _hover={{ backgroundColor: 'purple.700' }}
        >
          Run Agent
        </Button>
      </HStack>

      <DeleteModal
        isOpen={customerToRemove !== null}
        onClose={handleCloseModal}
        title="Remove assigned customer?"
        message={REMOVE_CUSTOMER_MESSAGE}
        onConfirm={handleConfirmRemove}
        confirmLabel="Remove"
      />
    </VStack>
  );
};

export default DetailsStage;

